/**
 * Locked-mode host resolution (SCH-37).
 *
 * When self-schedule one_on_one has >1 interviewers the Host field is
 * locked — the recruiter does NOT choose it. The value is decided here:
 *
 *   1. Organizer, if they can host Zoom.
 *   2. Else the first picked interviewer who can host Zoom. The Zoom
 *      meeting owner need not be the interviewer the candidate later
 *      picks (BE `validate_host` accepts any picked interviewer); we just
 *      need *a* valid owner so the meeting can be created.
 *   3. Else '' — nobody can host. Caller must drop Zoom as a location
 *      (disable it in the Location dropdown / fall back if selected).
 *
 * "Can host" === `!isHostOptionDisabled` — the SAME predicate the Location
 * dropdown uses to gate Zoom, so host preload and Zoom availability can
 * never disagree. Unknown zoom status counts as "can host" so we don't
 * strip before the BE status has loaded (matches existing wizard
 * behaviour for the non-locked path).
 */
import {
  isHostOptionDisabled,
  type HostZoomStatus,
} from '../fields/HostRenders';

export interface LockedHostOrganizer {
  id: number | string;
  zoomStatus?: HostZoomStatus;
}

export interface LockedHostParticipant {
  value: string;
  zoomStatus?: HostZoomStatus;
}

export interface ResolveLockedHostIdArgs {
  organizer?: LockedHostOrganizer;
  /** Picked interviewers, in selection order — the fallback pool. */
  participantOptions: LockedHostParticipant[];
}

export function resolveLockedHostId({
  organizer,
  participantOptions,
}: ResolveLockedHostIdArgs): string {
  if (organizer && !isHostOptionDisabled(organizer.zoomStatus)) {
    return String(organizer.id);
  }
  const firstValid = participantOptions.find(
    (o) => o.value && !isHostOptionDisabled(o.zoomStatus)
  );
  return firstValid?.value ?? '';
}
